<template>
  <div class="onboarding">
    <transition name="fade" mode="out-in">
      <component :is="currentPage.component" :nextOnboardingPage="nextPage" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, shallowRef } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'

import { StorageKeys } from '@/helpers/storage.keys'
import { auth } from '@/services/auth'
import { storage } from '@/helpers/storage'
import { Mixpanel } from '@/analytics/mixpanel'
import { maybeSyncAnalyticsIds } from '@/analytics/logic'
import { onboardingPlanCcCaptureV2_20676EVariant } from '@/services/ab/experiments'
import {
  getKnownContentOriginSlug,
  loadInterestBook,
} from '@/helpers/billingSourceBook'
import { Sentry } from '@/services/sentry'
import { clearPlanCardCapture20676 } from '@/models/onboarding.plan-card-capture-20676'
import { isHardGateBilling20516Active } from '@/models/hardGateBilling20516'
import { playerSession } from '@/services/player'

import Questionnaire from '@/app/components/onboarding/pages/Questionnaire.vue'
import SignupWrapper from '@/app/components/onboarding/pages/SignupWrapper.vue'
import Trial from '@/app/components/onboarding/pages/Trial.vue'

const router = useRouter()

const pages = shallowRef([
  { name: 'signup', component: SignupWrapper },
  { name: 'questionnaire', component: Questionnaire },
  { name: 'trial', component: Trial },
])

const currentPageIndex = ref(0)
const currentPage = computed(() => {
  return pages.value[currentPageIndex.value]
})

onBeforeMount(async () => {
  if (auth.loggedIn() && !auth.isTemporaryUser()) {
    // Skip the onboarding if the user is logged in and
    // doesn't have a position saved (we clear the position
    // when the user has finished the questionnaire).
    if (!storage.getItem(StorageKeys.questionnairePosition)) {
      router.replace({ name: 'discover' })
      return
    }

    // Skip to the questionnaire if the user is logged in
    // and has a saved position.
    currentPageIndex.value = pages.value.findIndex(
      (page) => page.name === 'questionnaire',
    )
  }

  trackPageView()
})

async function nextPage(): Promise<void> {
  if (currentPageIndex.value + 1 < pages.value.length) {
    currentPageIndex.value++
    await maybeSyncAnalyticsIds()
    trackPageView()
    return
  }

  const finalRoute = await getFinalOnboardingRoute()

  // Prepare the First Guide popup.
  // For variant B of #20676 we send users who started a paid trial and for whom we
  // resolved a "content origin" (book they came from) straight to
  // (see getFinalOnboardingRoute below) the book of interest.
  // Showing the popup for such users doesn't make sense, so we disable it.
  const shouldSkipFirstGuide =
    onboardingPlanCcCaptureV2_20676EVariant() === 'B' &&
    typeof finalRoute !== 'string' &&
    'name' in finalRoute &&
    finalRoute.name === 'book.page'
  // The first-guide popup opens a book. A gated free user would bounce
  // straight back to billing, so don't offer it.
  if (!shouldSkipFirstGuide && !isHardGateBilling20516Active()) {
    storage.setItem(StorageKeys.offerGuide, 'true')
  }

  Mixpanel.trackOnboardingComplete()

  // Cleanup billing plan selection and title for the onboarding plan credit card
  // capture experiment (#20676).
  clearPlanCardCapture20676()

  if (router.resolve(finalRoute).meta.isDocContentPage) {
    playerSession.value.setNextArrival('redirect')
  }
  router.replace(finalRoute)
}

/**
 * Resolve the end-of-onboarding destination for
 * exp_onboarding_plan_cc_capture_v2_20676 (#20676).
 *
 * We re-check everything from reliable sources instead of passing the whole
 * state through the Questionnaire:
 *   - `sf_first_page` from local storage
 *   - the locally stored user's plan status from `user` object.
 */
async function getFinalOnboardingRoute(): Promise<RouteLocationRaw> {
  if (onboardingPlanCcCaptureV2_20676EVariant() !== 'B') {
    return { name: 'billing' }
  }

  // Free users always land on the billing page, so we don't attempt resolving the
  // content origin for them (again).
  if (auth.isFreePlan()) {
    return { name: 'billing' }
  }

  const slug = getKnownContentOriginSlug()
  if (slug) {
    try {
      await loadInterestBook(slug)
      return { name: 'book.page', params: { url_slug: slug } }
    } catch (error) {
      Sentry.withScope((scope) => {
        scope.setTag('experiment', 'exp_onboarding_plan_cc_capture_v2_20676')
        scope.setExtra('slug', slug)
        Sentry.captureMessage(
          'exp_onboarding_plan_cc_capture_v2_20676: content origin unresolvable',
        )
      })
      return { name: 'billing' }
    }
  }

  return { name: 'discover' }
}

function trackPageView(): void {
  // The questionnaire page has its own logic
  // for tracking page views (because it has
  // many sub pages).
  if (currentPage.value.name === 'questionnaire') {
    return
  }

  Mixpanel.trackOnboardingPageView(currentPage.value.name)
}
</script>

<style lang="scss" scoped>
.onboarding {
  --onboarding-padding: 24px;
  --onboarding-outer-width: 480px;
  --onboarding-inner-width: 360px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  background: $c-bg;
}

// Highlight tag that can be used inside
// the `title` and `subtitle` fields in the
// page configuration.
:deep(h1 span:not([class])),
:deep(h2 span:not([class])) {
  background: $c-yellow;
  padding-inline: 3px;
  padding-block: 4px 1px;
}

// Tag for adding a footnote to a
// `subtitle` paragraph.
:deep(span.source) {
  display: block;
  margin-top: 12px;
  font-size: 14px;
  letter-spacing: -0.02em;
  color: $c-black-6;
}
</style>
